"use client";

import type {
  TooltipContentProps,
  TooltipPortalProps,
  TooltipProps as RadixTooltipProps,
} from "@radix-ui/react-tooltip";
import {
  Arrow,
  Content,
  Portal,
  Provider as TooltipProvider,
  Root,
  Trigger,
} from "@radix-ui/react-tooltip";
import type { ComponentRef, ReactNode, Ref } from "react";
import { useEffect, useState } from "react";

import { useMediaQuery } from "../../hooks";
import { cn } from "../../utils";

export interface TooltipProps
  extends Omit<TooltipContentProps, "content">, RadixTooltipProps {
  ref?: Ref<ComponentRef<typeof Content>>;
  content: ReactNode;
  disabled?: boolean;
  portalProps?: TooltipPortalProps;
}

const Tooltip = ({
  ref,
  open,
  content,
  children,
  disabled,
  className,
  portalProps,
  defaultOpen,
  delayDuration,
  disableHoverableContent,
  onOpenChange,
  ...props
}: TooltipProps) => {
  const { isSm } = useMediaQuery();

  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isSm) setIsOpen(newOpen);

    onOpenChange?.(newOpen);
  };

  const handleClick = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open);
    }
  }, [open]);

  if (disabled) return children;

  return (
    <Root
      defaultOpen={defaultOpen}
      open={isSm ? open : isOpen}
      delayDuration={delayDuration}
      disableHoverableContent={disableHoverableContent}
      onOpenChange={handleOpenChange}
    >
      <Trigger asChild tabIndex={-1} onClick={handleClick}>
        {children}
      </Trigger>
      <Portal {...portalProps}>
        <Content
          collisionPadding={8}
          className={cn(
            "ui:out ui:max-w-[30ch] ui:animate-fade ui:rounded-sm ui:border ui:border-divider ui:bg-buttonBg ui:p-2 ui:text-xs ui:shadow-sm ui:animate-duration-300 ui:sm:max-w-[50ch]",
            className,
          )}
          {...props}
          ref={ref}
        >
          {content}
          <Arrow asChild>
            <div
              className={cn(
                "ui:relative ui:border-x-8 ui:border-t-8 ui:border-x-transparent ui:border-t-divider",
                "ui:after:absolute ui:after:bottom-full ui:after:-translate-x-1/2 ui:after:-translate-y-0.5 ui:after:border-x-[7px] ui:after:border-t-[7px] ui:after:border-x-transparent ui:after:border-t-buttonBg",
              )}
            />
          </Arrow>
        </Content>
      </Portal>
    </Root>
  );
};

export { Tooltip, TooltipProvider };
